// React, Next
import { useContext } from 'react'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import Link from 'next/link'
import Modal from 'react-modal'

// MUI
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'

// Custom
import { translateHref } from 'assets/functions/links'
import { GlobalContext } from 'context/GlobalContextProvider'

// Config
import nextI18nextConfig from 'next-i18next.config'

// Style
import themeColors from '@/assets/theme/colors'
import textNoSelect from 'assets/theme/textNoSelect'
import classes from './GpdrModal-CSS'

type Props = {
  show: boolean
  onAccept: () => void
}

export default function GpdrModal(props: Props) {
  const { t } = useTranslation('common')
  const { screenDimensions } = useContext(GlobalContext)

  const router = useRouter()
  const currentLocale =
    router.query.locale || nextI18nextConfig.i18n.defaultLocale

  const customStyles = {
    overlay: {
      backgroundColor: 'rgba(0, 0, 0, 0.25)',
      zIndex: 2000
    },
    content: {
      position: 'fixed' as const,
      height: 'auto',
      width: screenDimensions.width > 700 ? 460 : '90%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      padding: 0,
      background: '#fff',
      border: `1px solid ${themeColors.gray.lighter}`,
      borderRadius: '8px',
      boxShadow: '0 8px 28px rgba(0,0,0,0.18)'
    }
  }

  const renderContent = () => (
    <Box
      sx={{
        ...classes.mainDiv,
        display: 'flex',
        flexDirection: 'column',
        padding: '1.75rem',
        width: '100%',
        boxSizing: 'border-box',
        ...textNoSelect
      }}
    >
      <Typography
        component="p"
        sx={{
          fontSize: '1.05rem',
          fontWeight: 700,
          color: themeColors.gray.dark,
          marginBottom: '0.75rem'
        }}
      >
        {t('gpdr.title')}
      </Typography>

      <Typography
        component="p"
        sx={{
          fontSize: '0.9rem',
          lineHeight: 1.7,
          color: themeColors.gray.defaultText,
          marginBottom: '1.5rem'
        }}
      >
        {t('gpdr.body')}
      </Typography>

      {/* Accept, then the policy for anyone who wants the detail. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1.25rem'
        }}
      >
        <Button
          variant="contained"
          onClick={props.onAccept}
          sx={{
            background: themeColors.success.main,
            borderRadius: '4px',
            padding: '0.6rem 1.5rem',
            fontWeight: 600,
            letterSpacing: '0.03em',
            boxShadow: 'none',
            '&:hover': {
              background: themeColors.success.darker,
              boxShadow: 'none'
            }
          }}
        >
          {t('gpdr.accept')}
        </Button>

        <Box
          component={Link}
          href={translateHref(currentLocale, 'privacy')}
          sx={{
            fontSize: '0.85rem',
            color: themeColors.gray.mid,
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' }
          }}
        >
          {t('gpdr.privacyLink')}
        </Box>
      </Box>
    </Box>
  )

  return (
    <Modal
      isOpen={props.show}
      ariaHideApp={false}
      style={customStyles}
      className="mymodal"
      closeTimeoutMS={200}
    >
      {renderContent()}
    </Modal>
  )
}
